// src/encounter/structured-note/phrase-occurrence.tsx
//
// PROD-2824 · TS-5 · frame v2 + `?ef=phrasePieces` only. A saved phrase that carries PIECES (a pick-list,
// a blank, a chart link — the validated library's phrases all do) opens as an OCCURRENCE in place, under
// the line it was called from: the phrase's own words with its live pieces inline, filled where they
// stand. No panel, no modal (design desk, 5 Oct).
//
// THE PATTERN IS THE CLINICIAN DEMO'S, COPIED 1:1 (Samer's chosen reference: scrips-clinician-demo
// `src/views/soh/encounter/PhraseNote.tsx`, the phrase card), drawn with Signal DS primitives:
//   - the header: "Phrase · .trigger", the phrase's name, a draft tag, how many parts are left, Remove;
//   - the body: the phrase's words, a pick-list as a chip group (DS StatusChip in a pressed button), a
//     blank as a small field with its unit, a chart link as a block with "· from the chart", its "as of"
//     time, Refresh and Make plain text;
//   - F2 / Shift+F2 move between the parts to fill (Tab moves within one).
// One difference, because these lines are plain-text answers: the occurrence ends with COMPLETE, which
// puts the finished prose into the line. Complete is held until every part is filled AND every chart link
// has loaded; the reason shows under the phrase and is the button's description. A failed chart read
// keeps Complete held and says so — placeholder wording never enters the note (it would be frozen at sign).
//
// FOCUS: into the first part when the occurrence opens; back to the line after Complete or Remove (the
// host does that — it owns the line).
//
// One engine, the A&P note's (`?ef=phrasePieces`): `phraseInsertOps` turns the phrase DEFINITION into
// fresh OCCURRENCES (R1: nothing an author saved pre-answers this visit), `choosePick` / `fillBlank`
// answer them, and `exportPlainText` renders them exactly as the A&P plain-text copy does. A chart link
// is a SNAPSHOT: what the chart said, with its "as of" time and each line's source record id
// (`ChartResult.sourceId`) kept in the piece — provenance, as the A&P engine on main and Epic SmartLinks
// do. A phrase writes narrative only, never a diagnosis, order, charge or structured answer (rule 3).
//
// CHART PIECES — THE HOOK. A chart link's values draw through `renderChartPiece` when the host passes
// one; the default is its values alone (`chartValueText`: "Penicillin — High", or its honest R6 state) —
// the header above it names the chart and carries the one "as of" (design desk, 5 Oct; D-45 time). The chart-value card for phrase chart
// pieces is being built in signal-ds (with `note-pieces.ts` resolvers) by another lane: wire it HERE, at
// `renderChartPiece`. This file reads `note-pieces.ts` and never changes its chart queries or chart text.
import { phraseLabel, typeableTrigger } from '../lib/smart-phrase';
import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Button, PickListChip, StatusChip, Tag, TextArea, TextField } from '@scripsteam/scrips-signal-ds';
import type { DeltaOp } from '../lib/note-delta';
import {
  choosePick,
  embeddedPiece,
  fillBlank,
  isPieceOpen,
  pieceLabel,
  type BlankPiece,
  type ChartPiece,
  type Piece,
  type PickPiece,
} from '../lib/note-pieces';
import {
  chartValueText,
  draftHoldReasons,
  draftReady,
  draftText,
  stampText,
  withChartAsText,
  withPiece,
  type PhraseFillDraft,
} from './phrase-fill-model';
import { blankFit, chosenChip, occurrenceLayout } from './occurrence-layout';
import { cn } from '@/lib/utils';
import { PHRASE_DRAFT_PREFIX, useUnsavedNote } from '../services/unsaved-note';
import { useLeaveGuard } from '../services/use-leave-guard';

export interface PhraseOccurrenceProps {
  draft: PhraseFillDraft;
  onChange: (ops: DeltaOp[]) => void;
  /** Complete: the finished prose for the line. Called only when every part is filled and loaded. */
  onComplete: (text: string) => void;
  /** Remove: drop the phrase; nothing reaches the line. */
  onRemove: () => void;
  /** Refresh the chart link at op `index`: whether what it quotes changed, and the NEW pull time. */
  onRefreshChart: (index: number) => { changed: boolean; pulledAt?: string };
  /** THE CHART-PIECE HOOK (see the file header). Absent → the piece's values (`chartValueText`). */
  renderChartPiece?: (piece: ChartPiece) => ReactNode;
}

/** The stops F2 / Shift+F2 move between: one per part (the first chip, the field, Refresh). */
const FOCUS_ATTR = 'data-piece-focus';

/** F2 / Shift+F2 on the occurrence: focus the next / previous part (wrapping), from wherever focus is. */
function onPieceKeys(e: KeyboardEvent<HTMLElement>) {
  if (e.key !== 'F2') return;
  const stops = [...e.currentTarget.querySelectorAll<HTMLElement>(`[${FOCUS_ATTR}]`)];
  if (!stops.length) return;
  e.preventDefault();
  const active = document.activeElement;
  const cur = stops.findIndex((s) => !!active && (s === active || !!s.closest('[data-piece]')?.contains(active)));
  const next = cur >= 0 ? (cur + (e.shiftKey ? -1 : 1) + stops.length) % stops.length : e.shiftKey ? stops.length - 1 : 0;
  stops[next].focus();
}

/** The occurrence, drawn in place under the line the phrase was called from. */
export function PhraseOccurrence({ draft, onChange, onComplete, onRemove, onRefreshChart, renderChartPiece }: PhraseOccurrenceProps) {
  const ready = draftReady(draft.ops);
  const reasons = draftHoldReasons(draft.ops);
  const reasonId = useId();
  const root = useRef<HTMLDivElement>(null);
  // D-65: the line width a blank measures itself against (a blank wider than 60% takes its own line).
  const body = useRef<HTMLDivElement>(null);
  const [lineWidth, setLineWidth] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = body.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setLineWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // An open phrase lives only here until Complete: a reload or leaving the visit drops it, and the parts already filled
  // with it (dev repro, 10 Oct: a half-filled HPI phrase vanished on refresh, with no warning). Once the physician has
  // filled a part, the browser asks before the tab closes or reloads, and the in-app leave guard asks before a
  // navigation. An untouched phrase (nothing filled) has nothing to lose and never asks (Andrew, 10 Oct). A chart link
  // fills itself, so it does not count. How the draft saves is unchanged — the warning is all this adds.
  const draftKey = `${PHRASE_DRAFT_PREFIX}${useId()}`;
  const touched = draft.ops.some((op) => {
    const piece = embeddedPiece(op);
    return !!piece && piece.t !== 'chart' && !isPieceOpen(piece);
  });
  useLeaveGuard(touched);
  useEffect(() => {
    useUnsavedNote.getState().setDraftChanged(draftKey, touched);
    return () => useUnsavedNote.getState().setDraftChanged(draftKey, false);
  }, [draftKey, touched]);
  // Focus goes into the phrase's first part as it opens (a phrase with no part to fill: its Complete).
  useEffect(() => {
    const first = root.current?.querySelector<HTMLElement>(`[${FOCUS_ATTR}]`) ?? root.current?.querySelector<HTMLElement>('[data-occurrence-complete]');
    first?.focus();
  }, []);
  const set = (index: number, piece: Piece) => onChange(withPiece(draft.ops, index, piece));
  const open = draft.ops.filter((op) => {
    const piece = embeddedPiece(op);
    return !!piece && isPieceOpen(piece);
  }).length;
  // No overflow clip on the panel: a long pick's list (PROD-3171) opens below the chip and runs past the panel into the
  // canvas scroll area, as every other pick list in the note does. A clip cut it at the panel's bottom edge.
  return (
    <div
      ref={root}
      data-phrase-occurrence={draft.macro.trigger}
      role="group"
      aria-label={`Phrase “${phraseLabel(draft.macro)}”`}
      onKeyDown={onPieceKeys}
      className="relative flex flex-col gap-2 rounded-lg border border-solid border-border-default border-l-3 border-l-brand-primary bg-surface-card p-4 shadow-2"
    >
      <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
        {/* PROD-3170 (design desk, 9 Oct): the eyebrow stays in capitals, but the trigger is shown as the clinician types
            it (".pcp-cc", the `.` menu's form), in monospace, never capitalised by the eyebrow. */}
        <span className="flex flex-row items-baseline gap-1 whitespace-nowrap">
          <span className="font-sans text-eyebrow font-bold uppercase tracking-eyebrow text-brand-primary">Phrase ·</span>
          <span data-phrase-trigger className="font-mono text-eyebrow font-semibold text-brand-primary">
            .{typeableTrigger(draft.macro.trigger)}
          </span>
        </span>
        <span className="font-sans text-size-sm font-semibold text-text-primary">{phraseLabel(draft.macro)}</span>
        <span data-phrase-draft>
          <Tag>Draft · not in the note until you select Complete</Tag>
        </span>
        {/* The status and the two actions are ONE cluster pushed to the right, so when the header wraps
            (a long phrase name, a narrow column) Complete and Remove stay right-aligned together
            (design desk advisory, 5 Oct). */}
        <span data-occurrence-actions className="ml-auto flex flex-row items-center gap-2">
          <span data-occurrence-status className="whitespace-nowrap font-sans text-size-xs text-text-secondary">
            {open ? `${open} to complete` : 'Ready to complete'}
          </span>
          <span title={ready ? undefined : reasons.join(' ')}>
            <Button
              variant="primary"
              size="sm"
              data-occurrence-complete=""
              disabled={!ready}
              aria-describedby={ready ? undefined : reasonId}
              onClick={() => onComplete(draftText(draft.ops))}
            >
              Complete
            </Button>
          </span>
          <Button variant="tertiary" size="sm" onClick={onRemove}>
            Remove
          </Button>
        </span>
      </div>
      <div
        ref={body}
        data-phrase-body
        className="flex flex-row flex-wrap items-center gap-x-1 gap-y-2 font-sans text-size-sm leading-normal text-text-primary"
      >
        {occurrenceLayout(draft.ops).map((item) => {
          if (item.kind === 'text') {
            // A line break inside the phrase starts a new row of the occurrence (the body wraps as a row).
            // D-65 (desk on ac16250): a space never starts a line — each run of words is its own flex item,
            // the gap already separates it from the part before, so its leading whitespace is dropped.
            return item.text.split('\n').map((line, j) => {
              const t = line.replace(/^\s+/, '');
              return [
                j > 0 ? <span key={`${item.key}-br-${j}`} aria-hidden data-phrase-break className="basis-full" /> : null,
                t ? (
                  <span key={`${item.key}-${j}`} className="whitespace-pre-wrap">
                    {t}
                  </span>
                ) : null,
              ];
            });
          }
          if (item.kind === 'findings') {
            // D-65: one finding per row — its label, then its options, aligned in a column.
            return (
              // A table, not a `grid-cols-[max-content_1fr]` grid: the PA styles with named classes only
              // (check-no-arbitrary-classes). The label cell is as wide as its longest label (w-px +
              // nowrap = max-content), the options take the rest — the same two aligned columns.
              <div key={item.key} data-finding-rows className="table basis-full">
                {item.rows.map(({ index, piece }) => (
                  <div key={piece.id} className="table-row">
                    <span data-finding-label className="table-cell w-px whitespace-nowrap py-px pr-3 align-middle text-text-primary">
                      {piece.label}
                    </span>
                    <span className="table-cell py-px align-middle">
                      <PickChips piece={piece} onChoose={(code) => set(index, choosePick(piece, code))} />
                    </span>
                  </div>
                ))}
              </div>
            );
          }
          const { index, piece, suffix } = item;
          if (piece.t === 'chart') {
            // A chart link is a block of its own line; a separator after it has nothing to sit beside.
            return (
              <ChartBlock
                key={item.key}
                piece={piece}
                renderChartPiece={renderChartPiece}
                onRefresh={() => onRefreshChart(index)}
                onPlain={() => onChange(withChartAsText(draft.ops, index))}
              />
            );
          }
          // D-65: a separator after a part stays on that part's line — never at the start of the next. A
          // pick-list carries it after its LAST chip (inside its wrap), so it follows the chip it belongs to.
          if (piece.t === 'pick') {
            return <PickChips key={item.key} piece={piece} suffix={suffix} onChoose={(code) => set(index, choosePick(piece, code))} />;
          }
          const control = <BlankInput piece={piece} lineWidth={lineWidth} onFill={(v) => set(index, fillBlank(piece, v))} />;
          return suffix ? (
            <span key={item.key} data-piece-with-suffix className={cn('inline-flex flex-row items-center', blankFit(piece, lineWidth).full && 'basis-full')}>
              {control}
              <span>{suffix}</span>
            </span>
          ) : (
            <Fragment key={item.key}>{control}</Fragment>
          );
        })}
      </div>
      {!ready && (
        <p id={reasonId} data-occurrence-reasons className="m-0 font-sans text-size-xs text-text-secondary">
          {`To complete: ${reasons.join(' ')}`}
        </p>
      )}
    </div>
  );
}

/** ENC-L2 (design desk follow-up 3, PROD-3171): up to this many options a pick is a row of chips; past it, the DS
 *  PickListChip (type-to-filter, the D-76 scrolling list), never a wall of chips (the chief complaint offers 40). */
export const PICK_CHIPS_MAX = 8;

function PickChips({ piece, onChoose, suffix }: { piece: PickPiece; onChoose: (code: string) => void; suffix?: string }) {
  if (piece.options.length > PICK_CHIPS_MAX) return <PickList piece={piece} onChoose={onChoose} suffix={suffix} />;
  const chosen = new Set(piece.chosen ?? []);
  return (
    <span data-piece="pick" role="group" aria-label={piece.label} className="inline-flex flex-row flex-wrap items-center">
      {piece.options.map((o, i) => (
        // The DS chip inside a plain button (the chip row's pattern): chosen per `chosenChip`, the rest neutral soft.
        <button
          key={o.code}
          type="button"
          aria-pressed={chosen.has(o.code)}
          {...(i === 0 ? { [FOCUS_ATTR]: '' } : {})}
          onClick={() => onChoose(o.code)}
          // D-65 / WCAG 2.2 2.5.8: the hit area is at least 24×24 px — the chip (16px tall) keeps its look,
          // the button's own padding makes the target.
          className="inline-flex min-h-6 min-w-6 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-1 hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand-primary"
        >
          <StatusChip {...(chosen.has(o.code) ? chosenChip(piece, o.code) : { semantic: 'neutral' as const, tone: 'soft' as const })}>
            {/* D-65 (desk on ac16250): the check's slot is kept in EVERY state — invisible and hidden
                from assistive tech when not chosen — so choosing an option never moves the others. */}
            {/* A no-break space: the chip is a flex row, where a plain space between items collapses. */}
            {chosen.has(o.code) ? (
              // The check is what the eye reads; assistive tech hears the state from aria-pressed.
              <span data-check aria-hidden>
                {'✓\u00a0'}
              </span>
            ) : (
              <span data-check-slot aria-hidden className="invisible">
                {'✓\u00a0'}
              </span>
            )}
            {o.display}
          </StatusChip>
        </button>
      ))}
      {suffix ? <span data-piece-suffix>{suffix}</span> : null}
    </span>
  );
}

/** A long pick as the DS PickListChip. A choice goes through the same `onChoose(code)` as a chip click, so single and
 *  multi (with exclusive answers) behave exactly as the chips do. */
function PickList({ piece, onChoose, suffix }: { piece: PickPiece; onChoose: (code: string) => void; suffix?: string }) {
  const wrap = useRef<HTMLSpanElement>(null);
  // F2 / Shift+F2 move part to part: the list's own button is this part's stop.
  useLayoutEffect(() => {
    wrap.current?.querySelector('button')?.setAttribute(FOCUS_ATTR, '');
  });
  const options = piece.options.map((o) => ({ value: o.code, label: o.display }));
  const chosen = piece.chosen ?? [];
  // Every phrase pick is required (it stays open until chosen, and Complete waits for it), so the closed
  // list reads as a required blank, "*** <label>", like the text pieces' placeholders — not the note's
  // optional "Not asked · select" (design desk on #682, 9 Oct).
  return (
    <span ref={wrap} data-piece="pick" data-pick-list className="inline-flex flex-row flex-wrap items-center">
      {piece.multi ? (
        <PickListChip
          multiple
          options={options}
          value={chosen}
          aria-label={piece.label}
          requiredLabel={piece.label}
          onChange={(next) => {
            // The list toggles one answer at a time; hand that one to the same rule a chip click uses.
            const toggled = [...next.filter((c) => !chosen.includes(c)), ...chosen.filter((c) => !next.includes(c))][0];
            if (toggled) onChoose(toggled);
          }}
        />
      ) : (
        <PickListChip options={options} value={chosen[0] ?? null} aria-label={piece.label} requiredLabel={piece.label} onChange={(code) => onChoose(code)} />
      )}
      {suffix ? <span data-piece-suffix>{suffix}</span> : null}
    </span>
  );
}

function BlankInput({ piece, onFill, lineWidth }: { piece: BlankPiece; onFill: (v: string) => void; lineWidth?: number }) {
  // The raw text is kept as typed (spaces included); the piece stores it trimmed (`fillBlank`).
  const [text, setText] = useState(piece.value ?? '');
  const fit = blankFit(piece, lineWidth);
  const label = piece.unit ? `${piece.label} (${piece.unit})` : piece.label;
  const placeholder = piece.unit ? '__' : piece.label;
  const change = (v: string) => {
    setText(v);
    onFill(v);
  };
  return (
    <span data-piece="blank" data-blank-full={fit.full ? '' : undefined} className={cn('inline-flex flex-row items-center gap-1', fit.full && 'basis-full')}>
      <span style={{ '--pa-blank-w': fit.width } as CSSProperties} className={cn('w-[var(--pa-blank-w)]', fit.full && 'flex-1')}>
        {fit.full ? (
          // D-65 (b): a blank on its own line never clips — the DS TextArea (one row, growing) wraps its
          // placeholder. A blank is one line of the note, so a line break typed here becomes a space.
          <TextArea
            rows={1}
            autoGrow
            {...{ [FOCUS_ATTR]: '' }}
            aria-label={label}
            placeholder={placeholder}
            value={text}
            onChange={(e) => change(e.target.value.replace(/\n+/g, ' '))}
          />
        ) : (
          <TextField
            size="compact"
            type="text"
            {...{ [FOCUS_ATTR]: '' }}
            aria-label={label}
            placeholder={placeholder}
            value={text}
            onChange={(e) => change(e.target.value)}
          />
        )}
      </span>
      {piece.unit && <span className="font-sans text-size-xs text-text-secondary">{piece.unit}</span>}
    </span>
  );
}

function ChartBlock({
  piece,
  renderChartPiece,
  onRefresh,
  onPlain,
}: {
  piece: ChartPiece;
  renderChartPiece?: (piece: ChartPiece) => ReactNode;
  onRefresh: () => { changed: boolean; pulledAt?: string };
  onPlain: () => void;
}) {
  const [refreshed, setRefreshed] = useState<{ changed: boolean; at: string } | null>(null);
  const s = piece.snapshot;
  const resolved = !isPieceOpen(piece);
  const at = resolved && s ? stampText(s.pulledAt) : undefined;
  const label = pieceLabel(piece);
  return (
    <div data-piece="chart" data-chart-query={piece.query} className="flex basis-full flex-col gap-2 py-1">
      <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1">
        <span className="whitespace-nowrap font-sans text-eyebrow font-bold uppercase tracking-eyebrow text-text-secondary">
          {label} · from the chart
        </span>
        {at && (
          <span data-as-of className="whitespace-nowrap font-sans text-size-xs text-text-secondary">
            as of {at}
          </span>
        )}
        {refreshed && (
          <span data-refreshed className="whitespace-nowrap font-sans text-size-xs text-text-secondary">
            {refreshed.changed ? `Updated from the chart at ${refreshed.at}` : `Unchanged since ${refreshed.at}`}
          </span>
        )}
        <span className="ml-auto flex flex-row items-center gap-1">
          <Button
            variant="tertiary"
            size="sm"
            {...{ [FOCUS_ATTR]: '' }}
            aria-label={`Refresh ${label}`}
            onClick={() => {
              // The time of THIS refresh, from the new snapshot — `piece` is still the render before it (Codex P2).
              const { changed, pulledAt } = onRefresh();
              setRefreshed({ changed, at: stampText(pulledAt) });
            }}
          >
            Refresh
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            // An unresolved link has nothing true to become: its placeholder never turns into text.
            disabled={!resolved}
            title={resolved ? undefined : `${label} has not loaded, so there is nothing to keep as text.`}
            onClick={onPlain}
          >
            Make plain text
          </Button>
        </span>
      </div>
      <span data-slot="phrase-chart-piece" data-chart-query={piece.query} className="font-sans text-size-sm text-text-primary">
        {renderChartPiece ? renderChartPiece(piece) : chartValueText(piece)}
      </span>
    </div>
  );
}
